import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  User, FileText, Download, Settings, CheckCircle, Clock,
  AlertCircle, ArrowRight, Shield, Award, Loader2
} from "lucide-react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";

const MemberDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [downloadingCert, setDownloadingCert] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile?.status === "active") {
      checkCertificate();
    }
  }, [profile]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/member/profile`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkCertificate = async () => {
    try {
      const response = await axios.get(`${API}/member/certificate`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      setCertificate(response.data);
    } catch (error) {
      // Certificate not generated yet - that's okay
      setCertificate(null);
    }
  };

  const handleDownloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const response = await axios.get(`${API}/member/certificate/download`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', certificate?.filename || 'CPSI_Certificate.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Certificate downloaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download certificate");
    } finally {
      setDownloadingCert(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      intent_received: { label: "Intent Received", color: "bg-yellow-100 text-yellow-800" },
      pending_application: { label: "Application Pending", color: "bg-blue-100 text-blue-800" },
      under_scrutiny: { label: "Under Review", color: "bg-purple-100 text-purple-800" },
      approved_pending_payment: { label: "Awaiting Payment", color: "bg-orange-100 text-orange-800" },
      payment_completed: { label: "Payment Received", color: "bg-teal-100 text-teal-800" },
      active: { label: "Active Member", color: "bg-teal-100 text-teal-800" },
      rejected: { label: "Application Rejected", color: "bg-red-100 text-red-800" },
      needs_correction: { label: "Corrections Needed", color: "bg-amber-100 text-amber-800" },
      need_clarification: { label: "Clarification Needed", color: "bg-amber-100 text-amber-800" },
    };
    
    const config = statusConfig[status] || { label: status, color: "bg-slate-100 text-slate-800" };
    return <Badge className={`${config.color} font-medium`}>{config.label}</Badge>;
  };

  const getNextAction = () => {
    const status = profile?.status || user?.status;
    
    switch (status) {
      case "intent_received":
      case "pending_application":
        return {
          title: "Complete Your Application",
          description: "Submit your full membership application with all required documents.",
          action: () => navigate("/application"),
          buttonText: "Start Application",
        };
      case "under_scrutiny":
        return {
          title: "Application Under Review",
          description: "Your application is being reviewed by the CPSI committee. We'll notify you once a decision is made.",
          action: null,
          buttonText: null,
        };
      case "needs_correction":
        return {
          title: "Corrections Required",
          description: "The CPSI team has requested some corrections to your application. Please review and update.",
          action: () => navigate("/application"),
          buttonText: "Update Application",
          variant: "warning",
        };
      case "approved_pending_payment":
        return {
          title: "Complete Payment",
          description: "Your application has been approved! Please complete the membership fee payment.",
          action: () => toast.info("Payment link will be sent to your email"),
          buttonText: "Pay Now",
        };
      case "payment_completed":
        return {
          title: "Payment Received",
          description: "Your payment has been received. Your membership will be activated shortly.",
          action: null,
          buttonText: null,
        };
      case "active":
        return {
          title: "Welcome to CPSI!",
          description: "You are an active member. Explore your member benefits and connect with the community.",
          action: () => navigate("/directory"),
          buttonText: "View Directory",
        };
      case "seeded":
      case "reviewed":
        return {
          title: "Welcome to CPSI!",
          description: "Your membership is being processed. You will receive an activation email shortly.",
          action: () => navigate("/directory"),
          buttonText: "View Directory",
        };
      case "need_clarification":
        return {
          title: "Clarification Required",
          description: "The committee needs additional information. Please review and update your application.",
          action: () => navigate("/application"),
          buttonText: "Update Application",
        };
      default:
        return {
          title: "Get Started",
          description: "Begin your membership journey.",
          action: () => navigate("/application"),
          buttonText: "Apply Now",
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const nextAction = getNextAction();
  const status = profile?.status || user?.status || "pending_application";
  const isActive = status === "active";
  const isSeeded = status === "seeded" || status === "reviewed";
  const isAdminOrSecretariat = user?.role === "admin" || user?.role === "secretariat";
  // For active/seeded members and admin/secretariat, hide application-related actions
  const hideApplicationActions = isActive || isSeeded || isAdminOrSecretariat;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="member-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                <User className="w-8 h-8 text-teal-700" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-2xl text-slate-900">
                  {user?.name || "Member"}
                </h1>
                <p className="text-slate-600">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(status)}
              {profile?.membership_number && (
                <Badge variant="outline" className="font-mono">
                  {profile.membership_number}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Next Action Card - hidden for admin/secretariat */}
            {!isAdminOrSecretariat && (
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isActive ? 'bg-teal-100' : 'bg-blue-100'
                  }`}>
                    {isActive ? (
                      <CheckCircle className="w-6 h-6 text-teal-700" />
                    ) : (
                      <Clock className="w-6 h-6 text-blue-700" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h2 className="font-heading font-semibold text-lg text-slate-900 mb-1">
                      {nextAction.title}
                    </h2>
                    <p className="text-slate-600 mb-4">
                      {nextAction.description}
                    </p>
                    {nextAction.action && (
                      <Button 
                        onClick={nextAction.action}
                        className="bg-slate-900 text-white hover:bg-slate-800"
                        data-testid="next-action-btn"
                      >
                        {nextAction.buttonText}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Verification Status */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Verification Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 rounded-lg ${
                    user?.email_verified ? 'bg-teal-50' : 'bg-amber-50'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {user?.email_verified ? (
                        <CheckCircle className="w-5 h-5 text-teal-700" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-700" />
                      )}
                      <span className="font-medium text-slate-900">Email</span>
                      <span className="text-xs text-red-600 font-medium">Required</span>
                    </div>
                    <p className={`text-sm ${
                      user?.email_verified ? 'text-teal-700' : 'text-amber-700'
                    }`}>
                      {user?.email_verified ? "Verified" : "Pending verification"}
                    </p>
                  </div>
                  
                  <div className={`p-4 rounded-lg ${
                    user?.phone_verified ? 'bg-teal-50' : 'bg-slate-50'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {user?.phone_verified ? (
                        <CheckCircle className="w-5 h-5 text-teal-700" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-slate-400" />
                      )}
                      <span className="font-medium text-slate-900">WhatsApp</span>
                      <span className="text-xs text-slate-500">Optional</span>
                    </div>
                    <p className={`text-sm ${
                      user?.phone_verified ? 'text-teal-700' : 'text-slate-500'
                    }`}>
                      {user?.phone_verified ? "Verified" : "Not verified"}
                    </p>
                  </div>
                </div>
                
                {/* Only show verification alert if EMAIL is not verified */}
                {!user?.email_verified && (
                  <Alert className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Please verify your email to proceed with your application.{" "}
                      <Link to="/verify" className="text-teal-700 hover:underline">
                        Verify now
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Application Summary - Only show for applicants who went through the form (not active/seeded members) */}
            {profile && !hideApplicationActions && status !== "intent_received" && status !== "pending_application" && (
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Application Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-slate-600">Membership Type</span>
                      <span className="font-medium text-slate-900 capitalize">
                        {profile.membership_type?.replace('_', ' ')}
                      </span>
                    </div>
                    {profile.submission_date && (
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Submitted On</span>
                        <span className="font-medium text-slate-900">
                          {new Date(profile.submission_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {profile.approval_date && (
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Approved On</span>
                        <span className="font-medium text-slate-900">
                          {new Date(profile.approval_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* Only show View/Update Application for non-active, non-seeded members who have applications */}
                {!hideApplicationActions && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate("/application")}
                    data-testid="view-application-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {status === "needs_correction" ? "Update Application" : "View Application"}
                  </Button>
                )}
                
                {(isActive || isSeeded) && (
                  <>
                    {certificate ? (
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-teal-700 border-teal-200 hover:bg-teal-50"
                        onClick={handleDownloadCertificate}
                        disabled={downloadingCert}
                        data-testid="download-certificate-btn"
                      >
                        {downloadingCert ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Award className="w-4 h-4 mr-2" />
                        )}
                        {downloadingCert ? "Downloading..." : "Download Certificate"}
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-slate-400"
                        disabled
                        data-testid="download-certificate-btn"
                      >
                        <Award className="w-4 h-4 mr-2" />
                        Certificate Pending
                      </Button>
                    )}
                  </>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/directory")}
                  data-testid="browse-directory-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Member Directory
                </Button>
              </CardContent>
            </Card>

            {/* Privacy Settings */}
            {(isActive || isSeeded) && (
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Privacy Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4">
                    Control what information is visible in the public directory.
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => alert("Privacy settings coming soon")}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Privacy
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Help */}
            <Card className="border border-slate-200 bg-slate-50">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-2">
                  Need Help?
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Contact our support team for any questions about your membership.
                </p>
                <a 
                  href="mailto:office@cpsindia.org.in"
                  className="text-sm text-teal-700 hover:text-teal-800 font-medium"
                >
                  office@cpsindia.org.in
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard;
